import { Container, Graphics } from "pixi.js"

const CELL_SIZE = 20

export function Pathing({
  subject,
  target,
  radius,
  minRadius = 0,
  isSolid,
  debug = false,
}) {
  const pathing = new Container()

  const graphics = new Graphics()

  pathing.addChild(graphics)

  let path = { nodes: null }

  let lastComputedTime = Date.now()

  const tick = () => {
    const r = Math.sqrt(
      Math.pow(target.x - subject.x, 2) + Math.pow(target.y - subject.y, 2),
    )
    if (r > radius || r < minRadius) {
      graphics.clear()
      path.nodes = null
      return
    }

    const delay = r < radius / 2 ? 500 : 750 // Recalculate less often when far away
    if (Date.now() - lastComputedTime < delay) return
    graphics.clear()

    const cx = (target.x + subject.x) / 2
    const cy = (target.y + subject.y) / 2

    const cellsX = Math.ceil((radius * 1.75) / CELL_SIZE)
    const cellsY = Math.ceil((radius * 1.75) / CELL_SIZE)

    const gridX = cx - (cellsX / 2) * CELL_SIZE
    const gridY = cy - (cellsY / 2) * CELL_SIZE

    const start = [
      Math.floor((subject.y - gridY) / CELL_SIZE),
      Math.floor((subject.x - gridX) / CELL_SIZE),
    ]
    const end = [
      Math.floor((target.y - gridY) / CELL_SIZE),
      Math.floor((target.x - gridX) / CELL_SIZE),
    ]

    // Generate a 2d array of cells between the subject and target
    const cells = [...Array(cellsY)].map((_, yc) =>
      [...Array(cellsX)].map((_, yx) => {
        if (yc === start[0] && yx === start[1]) return 0
        if (yc === end[0] && yx === end[1]) return 0

        const x = gridX + yx * CELL_SIZE
        const y = gridY + yc * CELL_SIZE

        return [
          //   { x: x + CELL_SIZE / 2, y: y + CELL_SIZE / 2 },
          { x, y },
          { x: x + CELL_SIZE, y },
          { x: x + CELL_SIZE, y: y + CELL_SIZE },
          { x, y: y + CELL_SIZE },
        ].some((c) => isSolid(c))
          ? 1
          : 0
      }),
    )

    const gridPath = aStar(cells, start, end)

    path.nodes = gridPath?.map(([y, x]) => [
      gridY + y * CELL_SIZE + CELL_SIZE / 2,
      gridX + x * CELL_SIZE + CELL_SIZE / 2,
    ])

    if (debug) {
      for (let y = 0; y < cells.length; ++y) {
        for (let x = 0; x < cells[y].length; ++x) {
          const solid = cells[y][x]
          const isStart = x === start[1] && y === start[0]
          const isEnd = x === end[1] && y === end[0]
          const isPath = gridPath?.some(([py, px]) => px === x && py === y)

          graphics
            .rect(
              gridX + x * CELL_SIZE,
              gridY + y * CELL_SIZE,
              CELL_SIZE,
              CELL_SIZE,
            )
            .fill(
              isStart
                ? "#00f3"
                : isEnd
                  ? "#0f03"
                  : isPath
                    ? "#fff3"
                    : solid
                      ? "#f003"
                      : "#0000",
            )
        }
      }
    }

    lastComputedTime = Date.now()
  }

  return { pathing, path, tick }
}

function aStar(grid, start, end) {
  // Convert arrays to strings for proper comparison
  const startStr = start.join(",")
  const endStr = end.join(",")

  const openSet = new Set([startStr])
  const closedSet = new Set()
  const cameFrom = {}
  const gScore = { [startStr]: 0 }
  const fScore = { [startStr]: heuristic(start, end) }

  while (openSet.size > 0) {
    // Find node with lowest fScore
    let currentStr = null
    let lowestFScore = Infinity
    for (const nodeStr of openSet) {
      if (fScore[nodeStr] < lowestFScore) {
        lowestFScore = fScore[nodeStr]
        currentStr = nodeStr
      }
    }

    const current = currentStr.split(",").map(Number)

    if (currentStr === endStr) {
      return reconstructPath(cameFrom, currentStr)
    }

    openSet.delete(currentStr)
    closedSet.add(currentStr)

    const neighbors = getNeighbors(grid, current)
    for (const neighbor of neighbors) {
      const neighborStr = neighbor.join(",")

      if (closedSet.has(neighborStr)) continue

      const tentativeGScore = gScore[currentStr] + 1

      if (!openSet.has(neighborStr) || tentativeGScore < gScore[neighborStr]) {
        cameFrom[neighborStr] = currentStr
        gScore[neighborStr] = tentativeGScore
        fScore[neighborStr] = tentativeGScore + heuristic(neighbor, end)

        if (!openSet.has(neighborStr)) {
          openSet.add(neighborStr)
        }
      }
    }
  }

  return null // No path found
}

function heuristic(a, b) {
  return Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1])) // Chebyshev distance
}

function getNeighbors(grid, node) {
  const neighbors = []
  const [row, col] = node
  const numRows = grid.length
  const numCols = grid[0].length

  if (row > 0 && grid[row - 1][col] === 0) neighbors.push([row - 1, col]) // Up
  if (row < numRows - 1 && grid[row + 1][col] === 0)
    neighbors.push([row + 1, col]) // Down
  if (col > 0 && grid[row][col - 1] === 0) neighbors.push([row, col - 1]) // Left
  if (col < numCols - 1 && grid[row][col + 1] === 0)
    neighbors.push([row, col + 1]) // Right

  if (row > 0 && col > 0 && grid[row - 1][col - 1] === 0)
    neighbors.push([row - 1, col - 1]) // Up-Left
  if (row > 0 && col < numCols - 1 && grid[row - 1][col + 1] === 0)
    neighbors.push([row - 1, col + 1]) // Up-Right
  if (row < numRows - 1 && col > 0 && grid[row + 1][col - 1] === 0)
    neighbors.push([row + 1, col - 1]) // Down-Left
  if (row < numRows - 1 && col < numCols - 1 && grid[row + 1][col + 1] === 0)
    neighbors.push([row + 1, col + 1]) // Down-Right

  return neighbors
}

function reconstructPath(cameFrom, currentStr) {
  const path = [currentStr.split(",").map(Number)]
  while (currentStr in cameFrom) {
    currentStr = cameFrom[currentStr]
    path.unshift(currentStr.split(",").map(Number))
  }
  return path
}
